import store from '../store'
import firebase, { db } from '../utils/Firebase'
import { hasFullAccess } from '../utils/Helpers'
import { loadStripe } from '@stripe/stripe-js'

// COACH PLANS
const FREEMIUM = 'price_1KlzRsEO5hTr6qAY4WRvQizn'
const PLAN_GROWTH = 'price_1QCrugEO5hTr6qAYRRhK12c2'
const NEW_PLAN_GROWTH = 'price_1UKNlDEO5hTr6qAYy6I2R0f2'
const PLAN_STANDARD_MONTHLY = 'price_1I1E9LEO5hTr6qAYzyjzDG3W'
const PLAN_STANDARD_ANNUAL = 'price_1Iv2CnEO5hTr6qAYjmP4Liyw'
const PLAN_PRO_MONTHLY = 'price_1I1E8PEO5hTr6qAYCCmxLm0N'
const PLAN_PRO_ANNUAL = 'price_1Iv2DREO5hTr6qAYLHD7AYAH'
const NEW_PLAN_PRO_MONTHLY = 'price_1RVMqwEO5hTr6qAYyhs3dDKx'
const NEW_PLAN_PRO_ANNUAL = 'price_1SHu7vEO5hTr6qAYeK8UcZ0u'
const PLAN_MASTER_MONTHLY = 'price_1RVMugEO5hTr6qAYKhj32aeV'
const PLAN_MASTER_ANNUAL = 'price_1RVMwaEO5hTr6qAYdjxv1AjJ'

// COMPANY PLANS
const PLAN_STANDARD_COMPANY_MONTHLY = 'price_1M2fKvEO5hTr6qAYOBCS2mu5'
const PLAN_STANDARD_COMPANY_ANNUAL = 'price_1M2fSwEO5hTr6qAYXJcCsn8f'
const PLAN_PRO_COMPANY_MONTHLY = 'price_1M2fUkEO5hTr6qAYOeKi1rlz'
const PLAN_PRO_COMPANY_ANNUAL = 'price_1M2fZAEO5hTr6qAYi0j8t16y'
const PLAN_GROWTH_COMPANY_MONTHLY = 'price_1UKNXZEO5hTr6qAYB3BcxZda'
const PLAN_GROWTH_COMPANY_ANNUAL = 'price_1UKNYAEO5hTr6qAYpI9PCzZY'
const NEW_PLAN_STANDARD_COMPANY_MONTHLY = 'price_1UKNZ8EO5hTr6qAYL5vFw80X'
const NEW_PLAN_STANDARD_COMPANY_ANNUAL = 'price_1UKNZqEO5hTr6qAYAIh4JOLi'

export const typePlans = {
    growth: [PLAN_GROWTH, NEW_PLAN_GROWTH],
    standard: [PLAN_STANDARD_MONTHLY, PLAN_STANDARD_ANNUAL, PLAN_STANDARD_COMPANY_MONTHLY, PLAN_STANDARD_COMPANY_ANNUAL],
    pro: [PLAN_PRO_MONTHLY, PLAN_PRO_ANNUAL, PLAN_PRO_COMPANY_MONTHLY, PLAN_PRO_COMPANY_ANNUAL, NEW_PLAN_PRO_MONTHLY, NEW_PLAN_PRO_ANNUAL],
    master: [PLAN_MASTER_MONTHLY, PLAN_MASTER_ANNUAL],
}

export const TIERS = ['growth', 'standard', 'pro', 'master']

const COMPANY_PLAN_TIERS = {
    [PLAN_GROWTH_COMPANY_MONTHLY]: 'standard',
    [PLAN_GROWTH_COMPANY_ANNUAL]: 'standard',
    [NEW_PLAN_STANDARD_COMPANY_MONTHLY]: 'pro',
    [NEW_PLAN_STANDARD_COMPANY_ANNUAL]: 'pro',
    [PLAN_STANDARD_COMPANY_MONTHLY]: 'pro',
    [PLAN_STANDARD_COMPANY_ANNUAL]: 'pro',
    [PLAN_PRO_COMPANY_MONTHLY]: 'master',
    [PLAN_PRO_COMPANY_ANNUAL]: 'master',
}

export const COMPANY_TIERS = ['standard', 'pro', 'master']

export const COMPANY_FULL_FEATURES = ['businessClients', 'businessBasicStatistics', 'businessAdvancedStatistics', 'businessCash']

export const FEATURES = {
    teamsPanel: 'standard',
    zapping: 'pro',
    bulkStudents: 'pro',
    playlistMode: 'standard',
    createGroup: 'standard',
    groupChat: 'standard',
    questionnaires: 'pro',
    habits: 'pro',
    competences: 'standard',
    dayView: 'standard',
    quickEdit: 'standard',
    wellnessStatistics: 'standard',
    injuriesStatistics: 'pro',
    competencesStatistics: 'pro',
    gpsStatistics: 'master',
    individualEvaluation: 'standard',
    groupEvaluation: 'pro',
    evaluationStatistics: 'pro',
    evaluationReports: 'master',
    businessClients: 'standard',
    businessBasicStatistics: 'pro',
    businessAdvancedStatistics: 'master',
    businessCash: 'master',
    studentStatistics: 'standard',
    studentEvaluations: 'standard',
    studentFiles: 'standard',
    chatMode: 'pro',
    shareImage: 'pro',
}

export const LIMITS = {}

export const DEFAULT_VIDEO_LIMITS = { growth: 3, standard: 5, pro: 8, master: 10, tolerance: 1, maxMB: 200 }

export const videoLimitsFrom = (config) => ({ ...DEFAULT_VIDEO_LIMITS, ...(config?.videoDurationLimits || {}) })

export const tierOfPriceId = (priceId) => {
    if (!priceId) return null
    if (COMPANY_PLAN_TIERS[priceId]) return COMPANY_PLAN_TIERS[priceId]
    return TIERS.find(tier => typePlans[tier].includes(priceId)) || null
}

export const tierOfAccount = (account) => {
    if (!account) return null
    if (account.overrides?.priceId) return tierOfPriceId(account.overrides.priceId)
    if (hasFullAccess(account)) return 'master'
    return tierOfPriceId(account.stripeAccount?.currentSubscription?.priceId)
}

export const tierAllows = (tier, feature) => {
    const required = FEATURES[feature]
    if (!required) return true
    if (!tier) return false
    return TIERS.indexOf(tier) >= TIERS.indexOf(required)
}

export const requiredTier = (feature) => FEATURES[feature] || null

export const limitForTier = (tier, key) => {
    const limits = LIMITS[key]
    if (!limits || !tier) return 0
    return limits[tier] ?? 0
}

const CARD_ERROR_KEYS = {
    card_declined: 'payment.card_declined',
    incorrect_number: 'payment.card_incorrect_number',
    invalid_number: 'payment.card_incorrect_number',
    expired_card: 'payment.card_expired',
    invalid_cvc: 'payment.card_invalid_cvc',
    incorrect_cvc: 'payment.card_invalid_cvc',
    invalid_expiry_month: 'payment.card_invalid_expiry',
    invalid_expiry_year: 'payment.card_invalid_expiry',
    processing_error: 'payment.card_processing_error',
    payment_intent_authentication_failure: 'payment.card_authentication_failed',
}

const isCardError = (message, stripeCode) => {
    const msg = message || ''
    return !!stripeCode
        || msg.includes('Card invalid (cleared)')
        || msg.includes('Your card was declined')
        || msg.includes('Your card number is incorrect')
        || msg.includes('Your card has expired')
        || msg.includes('security code is incorrect')
        || msg.includes('expiration')
}

export const savePaymentMethod = async (paymentMethodId) => {
    const save = firebase.functions().httpsCallable('httpPayments-savePaymentMethod')
    try {
        await save({ paymentMethodId })
        return { ok: true }
    } catch (err) {
        const stripeCode = err.details && err.details.stripeCode
        if (!stripeCode) return { ok: false, messageKey: 'payment.card_processing_error' }
        return { ok: false, messageKey: CARD_ERROR_KEYS[stripeCode] || 'payment.card_declined' }
    }
}

export const subscribeCoachPlan = async ({ plan, couponId = null }) => {
    const account = store.state.account
    const currentUser = store.state.currentUser
    const daysForTrial = account?.overrides?.skipTrial === true ? null : account?.stripeAccount?.fromFree ? 30 : plan?.daysForTrial || null

    const payload = {
        planId: plan.priceId,
        isFree: plan.isFree === true,
        addTrial: true,
        couponId,
        daysForTrial,
    }

    const subscribePlanCoach = firebase.functions().httpsCallable('httpPayments-subscribePlanCoach')

    try {
        const result = await subscribePlanCoach(payload)
        if (result.data.requiresAction) {
            const stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY)
            const { error } = await stripe.confirmCardPayment(result.data.paymentIntentClientSecret)
            if (error) {
                await db.collection('users').doc(currentUser.uid).update({ 'stripeAccount.paymentMethod': null })
                await store.dispatch('bindUserAccount', currentUser)
                const messageKey = CARD_ERROR_KEYS[error.decline_code] || CARD_ERROR_KEYS[error.code] || 'coach.payment_method_declined_redirect'
                return { status: 'card-error', messageKey }
            }
        }
        return { status: 'ok' }
    } catch (err) {
        const stripeCode = err.details && err.details.stripeCode
        const reason = err.details && err.details.reason

        if (reason === 'coupon') return { status: 'coupon-error', messageKey: 'payment_failed_messages.coupon_invalid' }

        if (isCardError(err.message, stripeCode)) {
            await db.collection('users').doc(currentUser.uid).update({ 'stripeAccount.paymentMethod': null })
            await store.dispatch('bindUserAccount', currentUser)
            return { status: 'card-error', messageKey: CARD_ERROR_KEYS[stripeCode] || 'coach.payment_method_declined_redirect' }
        }

        return { status: 'error', messageKey: 'payment.subscription_failed' }
    }
}
